import { useState, useEffect, useMemo } from 'react';
import {
  getConsultants, createConsultant, updateConsultant, deleteConsultant,
  saveConsultantQontoCredentials, getQontoGieStatus,
} from '../services/airtable';
import { useToast } from '../contexts/ToastContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { VERCEL_FUNCTIONS } from '../config/vercel';

const EMPTY_FORM = {
  nom: '', statut: 'Actif', societe_propre: '', format_facture: '',
  gd_folder_id: '', dropbox_folder: '', adresse: '', code_postal: '', ville: '', siret: '',
};

export function useConsultantsManagement() {
  const { showToast } = useToast();

  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');

  const [showModal, setShowModal]       = useState(false);
  const [modalMode, setModalMode]       = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting]     = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete]       = useState(null);
  const [deleting, setDeleting]               = useState(false);

  const [showQontoModal, setShowQontoModal]     = useState(false);
  const [qontoTarget, setQontoTarget]           = useState(null);
  const [qontoLogin, setQontoLogin]             = useState('');
  const [qontoSecretKey, setQontoSecretKey]     = useState('');
  const [qontoSubmitting, setQontoSubmitting]   = useState(false);
  const [togglingId, setTogglingId]             = useState(null);
  const [gieQontoStatus, setGieQontoStatus]     = useState({ oauthConnected: false, webhookConnected: false });
  const [registeringWebhooks, setRegisteringWebhooks] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null); // { target, label } | null
  const [disconnecting, setDisconnecting]       = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [data, gieStatus] = await Promise.all([getConsultants(), getQontoGieStatus()]);
      setConsultants((data || []).map(c => ({
        id:                  c.id,
        nom:                 c.fields?.Nom              || '',
        statut:              c.fields?.Statut            || 'Actif',
        societe_propre:      c.fields?.['Société propre'] || '',
        format_facture:      c.fields?.['Format Facture'] || '',
        gd_folder_id:        c.fields?.GDFolderID        || '',
        dropbox_folder:      c.fields?.DropboxFolder     || '',
        adresse:             c.fields?.Adresse           || '',
        code_postal:         c.fields?.['Code postal']   || '',
        ville:               c.fields?.Ville             || '',
        siret:               c.fields?.SIRET             || '',
        qontoCredentialsSet: !!c.fields?.QontoCredentialsSet,
        qontoEinvoicingEnabled: !!c.fields?.QontoEinvoicingEnabled,
        qontoOAuthConnected: !!c.fields?.QontoOAuthConnected,
        qontoWebhookConnected: !!c.fields?.QontoWebhookConnected,
      })));
      setGieQontoStatus(gieStatus || { oauthConnected: false, webhookConnected: false });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredConsultants = useMemo(() => {
    if (!searchTerm) return consultants;
    const q = searchTerm.toLowerCase();
    return consultants.filter(c =>
      c.nom.toLowerCase().includes(q) ||
      c.siret.toLowerCase().includes(q)
    );
  }, [consultants, searchTerm]);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormData(mode === 'edit' && item
      ? {
          nom: item.nom, statut: item.statut, societe_propre: item.societe_propre,
          format_facture: item.format_facture, gd_folder_id: item.gd_folder_id,
          dropbox_folder: item.dropbox_folder, adresse: item.adresse,
          code_postal: item.code_postal, ville: item.ville, siret: item.siret,
        }
      : EMPTY_FORM
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await createConsultant(formData);
        showToast('Consultant créé avec succès', 'success');
      } else {
        await updateConsultant(selectedItem.id, formData);
        showToast('Consultant modifié avec succès', 'success');
      }
      await load();
      handleCloseModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteConsultant(itemToDelete.id);
      showToast('Consultant supprimé avec succès', 'success');
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // --- Identifiants Qonto par consultant ---

  const handleOpenQontoModal = (item) => {
    setQontoTarget(item);
    setQontoLogin('');
    setQontoSecretKey('');
    setShowQontoModal(true);
  };

  const handleCloseQontoModal = () => {
    setShowQontoModal(false);
    setQontoTarget(null);
    setQontoLogin('');
    setQontoSecretKey('');
  };

  const handleSubmitQontoCredentials = async (e) => {
    e.preventDefault();
    if (!qontoTarget) return;
    setQontoSubmitting(true);
    try {
      await saveConsultantQontoCredentials(qontoTarget.id, qontoLogin.trim(), qontoSecretKey.trim());
      showToast('Identifiants Qonto enregistrés avec succès', 'success');
      await load();
      handleCloseQontoModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setQontoSubmitting(false);
    }
  };

  const handleToggleEinvoicing = async (item) => {
    if (!item.qontoCredentialsSet && !item.qontoEinvoicingEnabled) {
      showToast('Enregistrez d\'abord les identifiants Qonto de ce consultant', 'error');
      return;
    }
    setTogglingId(item.id);
    try {
      await updateConsultant(item.id, { qonto_einvoicing_enabled: !item.qontoEinvoicingEnabled });
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // "target" = "gie" ou l'id d'un consultant. Ouvre l'écran de consentement Qonto dans un
  // nouvel onglet (pour ne pas perdre la page courante) — l'URL est obtenue en authentifié
  // via fetch (une navigation directe vers /api/qonto-oauth-start ne porterait pas le header
  // Authorization JWT de l'app). L'onglet est ouvert de façon synchrone, AVANT le await, pour
  // éviter qu'il soit bloqué comme popup par le navigateur ; son URL est renseignée une fois
  // récupérée.
  const handleConnectQontoOAuth = async (target) => {
    // Pas de "noopener" ici : on a besoin de la référence retournée pour renseigner son URL
    // une fois récupérée (avec noopener, window.open renvoie null par spec).
    const newTab = window.open('', '_blank');
    try {
      const response = await fetchWithAuth(VERCEL_FUNCTIONS.QONTO_OAUTH_START, {
        method: 'POST',
        body: JSON.stringify({ target }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Échec de la connexion Qonto');
      if (newTab) newTab.location.href = data.authorizationUrl;
    } catch (err) {
      if (newTab) newTab.close();
      showToast(err.message, 'error');
    }
  };

  // Crée/recrée les abonnements webhook Qonto pour toutes les organisations connectées (GIE +
  // consultants) — équivalent du script scripts/register-qonto-webhooks.js, exécuté ici côté
  // serveur via un bouton plutôt qu'en console.
  const handleRegisterWebhooks = async () => {
    setRegisteringWebhooks(true);
    try {
      const response = await fetchWithAuth(VERCEL_FUNCTIONS.QONTO_REGISTER_WEBHOOKS, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Échec de l\'enregistrement des webhooks');

      const okCount = data.results.filter(r => r.status === 'ok').length;
      const errors = data.results.filter(r => r.status === 'error');

      if (errors.length === 0) {
        showToast(`Webhooks enregistrés avec succès (${okCount}/${data.results.length})`, 'success');
      } else {
        showToast(`Webhooks : ${okCount}/${data.results.length} réussis — échec pour ${errors.map(e => e.label).join(', ')} (${errors[0].error})`, 'error');
      }
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  // Ouvre la confirmation de déconnexion ("gie" ou l'id d'un consultant + libellé affiché).
  const handleOpenDisconnectQonto = (target, label) => setDisconnectTarget({ target, label });
  const handleCloseDisconnectQonto = () => setDisconnectTarget(null);

  const handleConfirmDisconnectQonto = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      const response = await fetchWithAuth(VERCEL_FUNCTIONS.QONTO_OAUTH_DISCONNECT, {
        method: 'POST',
        body: JSON.stringify({ target: disconnectTarget.target }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Échec de la déconnexion Qonto');
      showToast(`${disconnectTarget.label} déconnecté des webhooks Qonto`, 'success');
      setDisconnectTarget(null);
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  return {
    consultants, filteredConsultants, loading, error,
    searchTerm, setSearchTerm,
    gieQontoStatus, registeringWebhooks, handleRegisterWebhooks,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    showQontoModal, qontoTarget, qontoLogin, setQontoLogin, qontoSecretKey, setQontoSecretKey, qontoSubmitting,
    handleOpenQontoModal, handleCloseQontoModal, handleSubmitQontoCredentials,
    togglingId, handleToggleEinvoicing,
    handleConnectQontoOAuth,
    disconnectTarget, disconnecting,
    handleOpenDisconnectQonto, handleCloseDisconnectQonto, handleConfirmDisconnectQonto,
  };
}
